/**
 * @file product-template.ts
 * @description Шаблон страницы товара (phase19.16 Р2а «Каталог и товар»;
 * db-design-r2a, ответы 2, 2а, 4; uiux-design-r2a §3; SB34–SB38, SB51).
 *
 * Шаблон — список секций v2 с элементами данных товара, один на магазин.
 * Здесь:
 *   - раскладки шаблона (`PRODUCT_TEMPLATE_LAYOUTS`) и стандартный шаблон
 *     `PRODUCT_TEMPLATE_DEFAULT()` = страница товара витрины до Р2а 1:1
 *     (ответ 4: «NULL = умолчание из кода», бэкфилла нет);
 *   - `checkProductTemplate` — проверка обязательных элементов (SB34) на
 *     результирующем состоянии: при записи (writer, в транзакции), переносе,
 *     дублировании и публикации (409 `site_view_publish_blocked`). Одна чистая
 *     функция на все места (ответ 2а); CHECK в БД не годится — правило на
 *     несколько строк.
 *
 * Приёмка Р2а (решение пользователя 2026-10-11, SB34/SB36): обязательных
 * три — название, цена и покупка, сведения о товаре. «Продавец» остаётся в
 * раскладках как обычный элемент (рядом со сведениями): его можно скрыть и
 * удалить, проверка о нём не сообщает.
 *
 * SB120 этап 1 (v3, db-design-sb120-stage1 §3): стандартный шаблон — тот же
 * вид в числах. Он равен подъёму прежнего шаблона v2 (тест сверяет на трёх
 * плотностях): «На телефоне плотнее» → доля телефона 0,3, крошки — XS
 * внутри (8 px × плотность), на телефоне вплотную. Поэтому `build` и
 * `PRODUCT_TEMPLATE_DEFAULT` принимают плотность магазина (нет — «Обычно»):
 * магазин без своего шаблона видит те же пиксели, что до v3.
 *
 * SB120 этап 3а (v4): элементы шаблона — без `typo` («Авто»), стандартный
 * шаблон равен подъёму шаблона v3 (тест upgrade-v4.test.ts на трёх плотностях).
 */

import { spaceStepPx } from "../site-theme/legacy-steps.js";
import { SITE_LIMITS } from "../site-theme/limits.js";
import type { SiteDensityInput } from "../site-theme/legacy-v1.js";
import { PRODUCT_ELEMENT_INFO } from "./contexts.js";
import { defaultSectionLook } from "./defaults.js";
import { el, header, section } from "./presets/build.js";
import {
  isElementHiddenSomewhere,
  isProductElementKind,
  PRODUCT_TEMPLATE_REQUIRED,
  type ProductElementKind,
  type SectionLook,
  type SectionPayload,
} from "./schema.js";
import { PHONE_TIGHTER_FACTOR } from "./upgrade-v3.js";

// ── Раскладки ──

/** Оформление блоков шаблона: «как у сайта», на телефоне плотнее (доля 0,3 — как v2 «плотнее»). */
function templateLook(space: Partial<SectionLook["space"]> = {}): SectionLook {
  return {
    ...defaultSectionLook(),
    space: { phoneFactor: PHONE_TIGHTER_FACTOR, overlap: 0, mergeWithPrev: false, ...space },
  };
}

/** Крошки — тонкой полосой без лишних отступов (как `px-section pt-4 pb-2` витрины). */
function breadcrumbsSection(density: SiteDensityInput): SectionPayload {
  const inner = spaceStepPx("xs", density);
  return section({
    look: templateLook({
      outerTop: 0,
      outerBottom: 0,
      innerTop: inner,
      innerBottom: inner,
      // v2 «XS, плотнее»: ступень ниже XS — 0.
      phone: { innerTop: 0, innerBottom: 0 },
    }),
    columns: [[el("breadcrumbs")]],
  });
}

/**
 * Сведения о товаре слева, продавец справа (принятое отличие: сведения ниже
 * покупки, uiux-design-r2a §3). Продавец с 2026-10-11 необязательный, но в
 * раскладках остаётся — как на витрине до Р2а.
 */
function factsSellerSection(): SectionPayload {
  return section({
    shares: [6, 6],
    valign: "top",
    look: templateLook(),
    columns: [[el("product-facts", { look: "rows" })], [el("product-seller", { look: "compact" })]],
  });
}

function reviewsSection(): SectionPayload {
  return section({ look: templateLook(), columns: [[el("product-reviews", { limit: 3 })]] });
}

function relatedSection(): SectionPayload {
  return section({
    header: header("Похожие товары"),
    look: templateLook(),
    columns: [[el("product-related", { limit: 4, perRow: 4, phoneLayout: "stack" })]],
  });
}

/**
 * Колонка покупки в порядке ProductPurchasePanel витрины до Р2а: бейджи,
 * название (H1, категория, рейтинг), короткое описание, метки строкой,
 * цена и покупка (фасовка, цена, количество, кнопка) — и описание.
 */
function buyColumn(withDescription: boolean) {
  return [
    el("product-badges"),
    el("product-title", { showRating: true }),
    el("product-short"),
    el("product-tags", { look: "row" }),
    el("product-buy"),
    ...(withDescription ? [el("product-description")] : []),
  ];
}

export const PRODUCT_TEMPLATE_LAYOUT_KEYS = [
  "product-layout-classic",
  "product-layout-photo-wide",
  "product-layout-narrow-gallery",
] as const;
export type ProductTemplateLayoutKey = (typeof PRODUCT_TEMPLATE_LAYOUT_KEYS)[number];

export interface ProductTemplateLayout {
  readonly key: ProductTemplateLayoutKey;
  readonly title: string;
  readonly hint: string;
  /** Все блоки раскладки (каждый вызов — новые объекты); плотность магазина — для XS крошек (нет — «Обычно»). */
  build(density?: SiteDensityInput): SectionPayload[];
}

/** Реестр раскладок (OCP: новая раскладка — новая запись; заготовки группы «Страница товара» строятся из него). */
export const PRODUCT_TEMPLATE_LAYOUTS: readonly ProductTemplateLayout[] = [
  {
    key: "product-layout-classic",
    title: "Стандартная страница товара",
    hint: "Фото слева, название, цена и описание справа; ниже сведения, отзывы и похожие товары",
    build: (density = 1) => [
      breadcrumbsSection(density),
      section({
        shares: [6, 6],
        valign: "top",
        look: templateLook(),
        columns: [[el("product-gallery", { layout: "thumbs-below", zoom: false })], buyColumn(true)],
      }),
      factsSellerSection(),
      reviewsSection(),
      relatedSection(),
    ],
  },
  {
    key: "product-layout-photo-wide",
    title: "Фото во всю ширину",
    hint: "Крупные фото сверху, под ними покупка и описание в две колонки",
    build: (density = 1) => [
      breadcrumbsSection(density),
      section({ look: templateLook(), columns: [[el("product-gallery", { layout: "grid", zoom: true })]] }),
      section({ shares: [6, 6], valign: "top", look: templateLook(), columns: [buyColumn(false), [el("product-description")]] }),
      factsSellerSection(),
      reviewsSection(),
      relatedSection(),
    ],
  },
  {
    key: "product-layout-narrow-gallery",
    title: "Узкая галерея",
    hint: "Фото с миниатюрами сбоку в узкой колонке, широкая колонка для текста",
    build: (density = 1) => [
      breadcrumbsSection(density),
      section({
        shares: [4, 8],
        valign: "top",
        look: templateLook(),
        columns: [[el("product-gallery", { layout: "thumbs-side", zoom: true })], buyColumn(true)],
      }),
      factsSellerSection(),
      reviewsSection(),
      relatedSection(),
    ],
  },
];

/**
 * Стандартный шаблон = страница товара витрины до Р2а (сверено с HEAD
 * apps/web product/[slug]/page.tsx 2026-10-12): крошки → «Товар» в две
 * колонки 1:1 [галерея] / [бейджи, название с рейтингом, короткое описание,
 * метки строкой, цена и покупка, описание] → [сведения (состав — строкой списка, решение 2026-10-11)] / [продавец свёрнуто] → отзывы (3) →
 * «Похожие товары» (4). Проходит `checkProductTemplate` без ошибок (тест).
 */
export function PRODUCT_TEMPLATE_DEFAULT(density: SiteDensityInput = 1): SectionPayload[] {
  return PRODUCT_TEMPLATE_LAYOUTS[0]!.build(density);
}

/** Раскладка по ключу; undefined — нет такой. */
export function findProductTemplateLayout(key: string): ProductTemplateLayout | undefined {
  return PRODUCT_TEMPLATE_LAYOUTS.find((layout) => layout.key === key);
}

// ── Проверка шаблона ──

/** Блок шаблона для проверки: строка `SitePageBlock` или секция снимка. */
export interface ProductTemplateBlockInput {
  isEnabled: boolean;
  payload: SectionPayload;
}

export type TemplateIssueCode =
  | "required_missing"
  | "required_duplicate"
  | "required_hidden"
  | "element_duplicate"
  | "too_many_sections"
  | "extra_h1";

/** Замечание к шаблону: error — запись/публикация отклоняется; warning — показывается, не мешает. */
export interface TemplateIssue {
  level: "error" | "warning";
  code: TemplateIssueCode;
  /** Вид элемента, о котором речь; null — о шаблоне целиком. */
  kind: ProductElementKind | null;
  /** Номер блока в шаблоне (с 0); null — о шаблоне целиком. */
  blockIndex: number | null;
  /** id элемента в блоке; null — о блоке или шаблоне. */
  elementId: string | null;
  message: string;
}

interface Found {
  kind: ProductElementKind;
  blockIndex: number;
  elementId: string;
  hiddenBy: string | null;
}

const DEVICE_WORDS = { phone: "телефоне", tablet: "планшете", desktop: "ПК" } as const;

/** Почему элемент не виден покупателю: блок выключен, блок или элемент скрыт на устройстве. */
function hiddenReason(block: ProductTemplateBlockInput, element: SectionPayload["columns"]["items"][number]["elements"][number]): string | null {
  if (!block.isEnabled) return "блок скрыт";
  const off = (Object.keys(DEVICE_WORDS) as (keyof typeof DEVICE_WORDS)[]).filter((d) => !block.payload.look.visibility[d]);
  if (off.length > 0) return `блок не виден на ${off.map((d) => DEVICE_WORDS[d]).join(", ")}`;
  if (isElementHiddenSomewhere(element)) {
    const hidden = (Object.keys(DEVICE_WORDS) as (keyof typeof DEVICE_WORDS)[]).filter((d) => element.hide?.[d]);
    return `элемент скрыт на ${hidden.map((d) => DEVICE_WORDS[d]).join(", ")}`;
  }
  return null;
}

const label = (kind: ProductElementKind): string => PRODUCT_ELEMENT_INFO[kind].label;

/**
 * Проверка шаблона страницы товара (SB34, ответ 2а). Ошибки:
 *   - обязательного элемента (`PRODUCT_TEMPLATE_REQUIRED`: название, цена и
 *     покупка, сведения) нет / он больше одного / он не виден
 *     (выключенный блок, видимость блока, «скрыть» у элемента);
 *   - любой элемент данных товара больше одного;
 *   - блоков больше `SITE_LIMITS.productTemplateSections`.
 * Предупреждения: второй H1 (заголовок или шапка раздела уровня 1) — главный
 * заголовок страницы товара уже название (product-title всегда H1).
 */
export function checkProductTemplate(blocks: readonly ProductTemplateBlockInput[]): TemplateIssue[] {
  const issues: TemplateIssue[] = [];
  if (blocks.length > SITE_LIMITS.productTemplateSections) {
    issues.push({
      level: "error",
      code: "too_many_sections",
      kind: null,
      blockIndex: null,
      elementId: null,
      message: `В шаблоне страницы товара не больше ${SITE_LIMITS.productTemplateSections} блоков`,
    });
  }

  const found: Found[] = [];
  blocks.forEach((block, blockIndex) => {
    if (block.payload.header?.level === 1) {
      issues.push({
        level: "warning",
        code: "extra_h1",
        kind: null,
        blockIndex,
        elementId: null,
        message: "Название товара уже главный заголовок — сделайте шапку этого блока H2",
      });
    }
    for (const column of block.payload.columns.items) {
      for (const element of column.elements) {
        if (element.kind === "heading" && element.level === 1) {
          issues.push({
            level: "warning",
            code: "extra_h1",
            kind: null,
            blockIndex,
            elementId: element.id,
            message: "Название товара уже главный заголовок — сделайте этот заголовок H2",
          });
        }
        if (isProductElementKind(element.kind)) {
          found.push({ kind: element.kind, blockIndex, elementId: element.id, hiddenBy: hiddenReason(block, element) });
        }
      }
    }
  });

  const required = new Set<string>(PRODUCT_TEMPLATE_REQUIRED);
  for (const kind of PRODUCT_TEMPLATE_REQUIRED) {
    if (!found.some((f) => f.kind === kind)) {
      issues.push({
        level: "error",
        code: "required_missing",
        kind,
        blockIndex: null,
        elementId: null,
        message: `В шаблоне нет обязательного элемента «${label(kind)}»`,
      });
    }
  }

  const seen = new Set<string>();
  for (const f of found) {
    const isRequired = required.has(f.kind);
    if (seen.has(f.kind)) {
      issues.push({
        level: "error",
        code: isRequired ? "required_duplicate" : "element_duplicate",
        kind: f.kind,
        blockIndex: f.blockIndex,
        elementId: f.elementId,
        message: `Элемент «${label(f.kind)}» уже есть в шаблоне — он может быть только один`,
      });
      continue;
    }
    seen.add(f.kind);
    if (isRequired && f.hiddenBy) {
      issues.push({
        level: "error",
        code: "required_hidden",
        kind: f.kind,
        blockIndex: f.blockIndex,
        elementId: f.elementId,
        message: `«${label(f.kind)}» — обязательный элемент, он должен быть виден всегда: ${f.hiddenBy}`,
      });
    }
  }
  return issues;
}

/** Есть ли в замечаниях ошибки (запись и публикация отклоняются). */
export function hasTemplateErrors(issues: readonly TemplateIssue[]): boolean {
  return issues.some((issue) => issue.level === "error");
}
